import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function SetPasswordPage() {
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    if (pwd.length < 8) return setErr('A senha deve ter pelo menos 8 caracteres.');
    if (pwd !== pwd2) return setErr('As senhas não conferem.');

    setLoading(true);
    try {
      // 1) define a senha no Auth (token do invite já autenticou o usuário)
      const { error: upErr } = await supabase.auth.updateUser({ password: pwd });
      if (upErr) throw upErr;

      // 2) limpa o flag no perfil
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error: profErr } = await supabase
          .from('users')
          .update({ must_set_password: false })
          .eq('id', user.id);
        if (profErr) throw profErr;
      }

      navigate('/', { replace: true });
    } catch (e: any) {
      setErr(e?.message ?? 'Falha ao definir senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray via-white to-light-gray-alt dark:from-soft-black dark:via-deep-purple/20 dark:to-soft-black font-exo flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-darling-lilac via-transparent to-ocean-water" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-darling-lilac/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-ocean-water/20 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Logo e Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-darling-lilac/10 dark:bg-darling-lilac/20 mb-6">
            <Shield className="h-8 w-8 text-darling-lilac" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-exo font-black text-soft-black dark:text-white mb-2">
            Definir Senha
          </h1>
          
          <p className="text-soft-black/70 dark:text-light-gray/80 font-exo mb-2">
            Para sua segurança, defina uma senha forte
          </p>
          
          <p className="text-sm text-soft-black/60 dark:text-light-gray/70 font-exo">
            Esta será sua senha de acesso à plataforma
          </p>
        </div>

        {/* Form */}
        <div className="bg-white/80 dark:bg-soft-black/80 backdrop-blur-sm rounded-2xl border border-light-gray-alt dark:border-deep-purple/30 shadow-2xl p-8">
          <form onSubmit={save} className="space-y-6">
            {/* Password Field */}
            <div>
              <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                Nova senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                  placeholder="Mínimo 8 caracteres"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50 hover:text-darling-lilac transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50 hover:text-darling-lilac transition-colors" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                Confirmar senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50" />
                </div>
                <input
                  type={showPassword2 ? 'text' : 'password'}
                  required
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                  placeholder="Digite a senha novamente"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword2(!showPassword2)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword2 ? (
                    <EyeOff className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50 hover:text-darling-lilac transition-colors" />
                  ) : (
                    <Eye className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50 hover:text-darling-lilac transition-colors" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {err && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50"
              >
                <p className="text-sm text-red-600 dark:text-red-400 font-exo">
                  {err}
                </p>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-darling-lilac hover:bg-deep-purple disabled:bg-darling-lilac/50 text-white font-exo font-semibold transition-all duration-200 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4" />
                  Salvar senha
                </>
              )}
            </button>
          </form>

          {/* Security Info */}
          <div className="mt-6 pt-6 border-t border-light-gray-alt dark:border-deep-purple/30">
            <div className="text-xs text-soft-black/50 dark:text-light-gray/60 font-exo space-y-1">
              <p>• Use pelo menos 8 caracteres</p>
              <p>• Combine letras, números e símbolos</p>
              <p>• Evite informações pessoais óbvias</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-soft-black/40 dark:text-light-gray/50 font-exo">
            © {new Date().getFullYear()} CRO Brasil — Plataforma de Embaixadores
          </p>
        </div>
      </motion.div>
    </div>
  );
}